/**
 * "WHERE IS THE VEHICLE?" — AND HOW THE ANSWER GETS BACK TO THE SERVER.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * FLOYD-5. Deleting a ticket whose car is still in a bay produced:
 *
 *     Where is the vehicle?
 *     This vehicle is in a bay. Say where it goes before ending the visit.
 *
 * …and then stopped. `TicketKebabMenu` rendered `LOCATION_REQUIRED` as a
 * destructive toast and returned, so the question had no answer surface and
 * the ticket could not be deleted from the UI at all.
 *
 * The server had been waiting the whole time: `deleteTicketFamily` has always
 * taken `(ticketId, reason, locationKind, parkingSpotId)`, and
 * `LOCATION_REQUIRED` has always been in its known vocabulary. Only the
 * client half of the conversation was missing.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ONE AUTHORITY, ASKED TWICE
 * ═══════════════════════════════════════════════════════════════════════════
 * The obvious repair — move the car with `move_vehicle_location`, then delete
 * — is wrong. Those are two acts, and the second can refuse for a reason the
 * first knew nothing about: a technician resumes, a review appears. The car
 * ends up relocated for a deletion that never happened, which is precisely
 * the partial-human-action class the family-delete authority exists to end.
 *
 * So the SAME atomic call is retried with the location attached. The server
 * decides once, under its own lock, holding everything it needs.
 */

/** The location vocabulary the terminal authority accepts. */
export type TerminalLocationChoice = {
  kind: 'GENERAL_LOT' | 'PARKING_SPOT';
  spotId: string | null;
};

/**
 * The outcomes that mean "the server is asking where the car goes".
 *
 * `LOCATION_CONFLICT` is here as well as `LOCATION_REQUIRED`, because a spot
 * somebody else took between the question and the answer is the same
 * question again — not a failure to report and walk away from.
 */
export const DELETE_LOCATION_OUTCOMES = [
  'LOCATION_REQUIRED',
  'LOCATION_CONFLICT',
] as const;

/**
 * Should the location picker open for this outcome?
 *
 * Deliberately NOT "is this a refusal". A refusal about the WORK — someone is
 * still timing, a review is owed — must not raise a location question, both
 * because it is a non-sequitur and because answering it would imply the
 * deletion could then proceed. Those refusals keep their own message.
 *
 * Takes a plain string rather than the outcome union so an outcome the client
 * build does not yet know about is classified as "do not prompt" instead of
 * failing to compile or, worse, falling into a default that asks.
 */
export function shouldPromptForDeleteLocation(outcome: string): boolean {
  return (DELETE_LOCATION_OUTCOMES as readonly string[]).includes(outcome);
}

/**
 * Turn the picker's answer into the terminal location.
 *
 * `ParkingSpotPickerDialog` in `selectionOnly` mode reports `null` for the
 * General Lot and a spot id otherwise. An empty string is treated as the lot
 * as well: a blank id would otherwise become `PARKING_SPOT` with nothing to
 * point at, which the server refuses as malformed — a harder failure to read
 * than the answer the operator actually gave.
 */
export function toTerminalLocation(spotId: string | null | undefined): TerminalLocationChoice {
  const trimmed = typeof spotId === 'string' ? spotId.trim() : '';
  return trimmed
    ? { kind: 'PARKING_SPOT', spotId: trimmed }
    : { kind: 'GENERAL_LOT', spotId: null };
}
